import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  head: () => ({ meta: [
    { title: "Reset password — Michael's Painting" },
    { name: "robots", content: "noindex" },
  ] }),
  component: ResetPasswordRoute,
  errorComponent: ({ error }) => <div className="py-32 text-center text-red-400">{error.message}</div>,
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

function ResetPasswordRoute() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Supabase places the recovery session in the URL hash and hydrates it
    // automatically. Confirm a session is present before showing the form.
    const check = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) { setReady(true); return; }
      // Wait one tick for the SDK to consume the hash.
      setTimeout(async () => {
        const { data: d2 } = await supabase.auth.getSession();
        if (d2.session) setReady(true);
        else setError("This reset link is invalid or has expired. Request a new one from the sign-in page.");
      }, 400);
    };
    check();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { error: e2 } = await supabase.auth.updateUser({ password });
      if (e2) throw e2;
      setOk(true);
      setTimeout(() => navigate({ to: "/" }), 1500);
    } catch (e2) { setError((e2 as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-[#030303] text-white grid place-items-center px-6 py-24">
      <div className="w-full max-w-md border border-white/10 rounded-2xl bg-white/[0.02] p-8">
        <h1 className="font-serif text-3xl mb-2">Set a new password</h1>
        <p className="text-sm text-zinc-500 mb-6">Choose a new password for your account.</p>
        {ok ? (
          <p className="text-sm text-emerald-400">Password updated. Redirecting…</p>
        ) : !ready ? (
          <p className="text-sm text-zinc-400">{error ?? "Verifying reset link…"}</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
            <input type="password" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={busy} className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="size-4 animate-spin" />}
              Update password
            </button>
          </form>
        )}
      </div>
    </section>
  );
}